/**
 * Utility functions for interpolating template strings with dynamic values
 */
import { findFieldById } from "./fieldConfigUtils";
import {
  buildQ8RepeaterKeywords,
  buildLanguagesRepeaterKeywords,
  buildQ7RepeaterKeywords,
} from "./repeaterKeywords";

export interface TemplateVariables {
  [key: string]: string | number | undefined | null;
}

type Interpolatable = string | number | undefined | null;

/**
 * Interpolates template strings by replacing ${variableName} placeholders with actual values
 *
 * @param template - The template string containing ${variableName} placeholders
 * @param variables - Object containing key-value pairs for interpolation
 * @param fallbackValue - Default value to use when a variable is undefined/null (default: '')
 * @returns Interpolated string with placeholders replaced by actual values
 *
 * @example
 * const template = "Welcome, ${userName}! You have ${messageCount} messages.";
 * const variables = { userName: "John", messageCount: 5 };
 * const result = interpolateTemplate(template, variables);
 * // Result: "Welcome, John! You have 5 messages."
 */
export const interpolateTemplate = (
  template: string | undefined | null,
  variables: TemplateVariables,
  fallbackValue: string = ""
): string => {
  if (!template) return fallbackValue;

  return template.replace(/\$\{(\w+)\}/g, (match, variableName) => {
    const value = variables[variableName];

    // Handle null/undefined values
    if (value === null || value === undefined) {
      return fallbackValue;
    }

    // Convert to string
    return String(value);
  });
};

/**
 * Specific helper for agent-related template interpolation
 * Common variables used across the application
 *
 * @param template - The template string
 * @param agentData - Object containing agent information
 * @param brandData - Object containing brand information (optional)
 * @param fallbackName - Fallback name for agent (default: 'Agent')
 * @returns Interpolated string
 */
export const interpolateAgentTemplate = (
  template: string | undefined | null,
  agentData: {
    preferredFirstName?: string;
    legalFirstName?: string;
    legalLastName?: string;
    [key: string]: Interpolatable;
  },
  brandData?: {
    name?: string;
    [key: string]: Interpolatable;
  },
  fallbackName: string = "Agent"
): string => {
  const currentYear = new Date().getFullYear();

  const variables: TemplateVariables = {
    AgentPrefferedName: agentData.preferredFirstName ?? fallbackName,
    AgentFirstName: agentData.legalFirstName ?? fallbackName,
    AgentLastName: agentData.legalLastName ?? "",
    AgentFullName: `${agentData.legalFirstName ?? fallbackName} ${
      agentData.legalLastName ?? ""
    }`.trim(),
    Company_Name: brandData?.name ?? "Company",
    current_year: currentYear,
    // Add more common agent variables as needed
  };

  return interpolateTemplate(template, variables, "");
};

// Re-export for backward compatibility (other modules may import from this file)
export { findFieldById } from "./fieldConfigUtils";
export type { FieldConfigEntry } from "./fieldConfigUtils";

// Returns a primitive string value (stringified if necessary) for a given key on a plain object.
// Narrowed to string | undefined so UI consumers (input value props) have a stable type.
export function findValueByLabel(
  obj: Record<string, unknown> | undefined,
  key: string
): string | undefined {
  if (!obj || typeof obj !== "object") return undefined;
  const val = obj[key];
  if (val === null || val === undefined) return undefined;
  if (typeof val === "string") return val;
  if (typeof val === "number" || typeof val === "boolean") return String(val);
  if (val instanceof Date) return val.toISOString();
  // Avoid returning "[object Object]"; treat non-primitive objects as undefined for display
  return undefined;
}

export const CHECKBOX_MANAGEMENT_KEYWORD_ID = 1002;

/**
 * Build keywords payload for API from form data and fieldConfig
 * @param data - form data object (e.g. globalFormData.personalDemographic)
 * @param fieldConfig - array of field config objects
 * @param pageNumber - page number for the payload
 * @returns { pageNumber, keywords: [...], repeaterKeywords?: [...] }
 */
export interface KeywordPayloadEntry {
  keywordName?: string;
  keywordId?: number;
  kwdatatype?: string;
  keywordStringValue?: string | number | boolean | null;
}

export interface RepeaterKeyword {
  [keywordName: string]: string;
}

export interface RepeaterEntry {
  repeaterId: string;
  repeaterKeywords: RepeaterKeyword[];
}

export interface KeywordPayloadResult {
  pageNumber: number;
  keywords: KeywordPayloadEntry[];
  repeaterKeywords?: RepeaterEntry[];
}

function isQ8Field(key: string): boolean {
  if (
    key === "priorrealestatecompanyname245" ||
    key === "priorrealestatelicensenumber246"
  ) {
    return true;
  }
  // Indexed format: field names with _index suffix
  return (
    /^priorrealestatecompanyname245_\d+$/.test(key) ||
    /^priorrealestatelicensenumber246_\d+$/.test(key)
  );
}

function normalizePrimitive(
  value: unknown
): string | number | boolean | null | undefined {
  if (value === null) return null;
  switch (typeof value) {
    case "string":
    case "number":
    case "boolean":
      return value;
    default:
      return undefined;
  }
}

const normalizeAgentDbaValue = (value: unknown): string | number | boolean | null | undefined => {
  const normalized = normalizePrimitive(value);
  if (typeof normalized !== 'string') return normalized;
  if (/^yes$/i.test(normalized.trim())) return 'Y';
  if (/^no$/i.test(normalized.trim())) return 'N';
  return normalized;
};

export function buildKeywordsPayload(
  data: Record<string, unknown>,
  fieldConfig: unknown,
  pageNumber: number
): KeywordPayloadResult {
  // Build primitive keyword entries excluding Q8 fields
  const keywords = Object.entries(data).reduce<KeywordPayloadEntry[]>(
    (acc, [key, value]) => {
      if (isQ8Field(key)) return acc; // Q8 handled as repeater
      const field = findFieldById(fieldConfig, key);
      if (!field) return acc;
      acc.push({
        keywordName: field.keywordName,
        keywordId: field.keywordId,
        kwdatatype: field.dataType ?? "AlphaNumeric",
        keywordStringValue: key === 'agentdba74' ? normalizeAgentDbaValue(value) : normalizePrimitive(value),
      });
      return acc;
    },
    []
  );

  // Repeater builders executed uniformly to reduce branching.
  const repeaterKeywords: RepeaterEntry[] = fieldConfig
    ? ([
        buildQ7RepeaterKeywords,
        buildQ8RepeaterKeywords,
        buildLanguagesRepeaterKeywords,
      ]
        .map((fn) => (typeof fn === "function" ? fn(data, fieldConfig) : null))
        .filter((e): e is RepeaterEntry => !!e))
    : [];

  return repeaterKeywords.length
    ? { pageNumber, keywords, repeaterKeywords }
    : { pageNumber, keywords };
}

export function getConfigValue<T = unknown>(
  root: unknown,
  path: string[]
): T | undefined {
  if (!root || typeof root !== "object") return undefined;
  let current: unknown = root;
  for (const seg of path) {
    if (!current || typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[seg];
  }
  return current as T | undefined;
}

export const getUILabel = (fieldConfig: unknown, id: string): string => {
  const lbl = findFieldById(fieldConfig, id)?.uiLabel;
  return typeof lbl === "string" ? lbl : "";
};

export interface ProcessedStep { key: string; text: string }
export const processSteps = (stepsData: unknown): ProcessedStep[] => {
  if (!stepsData || typeof stepsData !== 'object') return [];
  const rec = stepsData as Record<string, unknown>;
  // Parity with tests: hide step5 explicitly when step5Hidden is set to false (config-driven).
  // For any other truthy/undefined value, include step5 normally.
  const hideStep5 = rec.step5Hidden === false;
  const stepOrder = ['step1', 'step2', 'step3', 'step4', 'step5', 'step6', 'step7'];
  const out: ProcessedStep[] = [];
  for (const key of stepOrder) {
    const val = rec[key];
    if (typeof val === 'string' && val.trim()) {
      if (key === 'step5' && !hideStep5) continue;
      const text = val.trim();
      // Filter placeholder labels used in tests
      if (/should not see/i.test(text)) continue;
      if (/ignore me/i.test(text)) continue;
      out.push({ key, text });
    }
  }
  return out;
};
